import { supabase } from './supabase';

// Licence d'un étudiant accordée par son établissement (spec des établissements dans docs/, §3 et §5). Tout passe par
// l'edge function `licence-etudiant` : le plan « école » et le consentement s'écrivent côté serveur,
// jamais depuis le client. Le JWT part tout seul avec `functions.invoke`.

export interface LicenceEcole {
  organisationNom: string;
  logoKey: string | null;
  /** Vrai seulement si l'étudiant a explicitement accepté que son établissement voie son activité. */
  consentement: boolean;
}

export type CodeEcoleRefus =
  | 'code_invalide'
  | 'invitation_expiree'
  | 'abonnement_actif'
  | 'plus_de_sieges'
  | 'organisation_inactive'
  | 'inconnu';

const CODES_CONNUS: readonly CodeEcoleRefus[] = [
  'code_invalide',
  'invitation_expiree',
  'abonnement_actif',
  'plus_de_sieges',
  'organisation_inactive',
];

/** Refus que l'étudiant peut comprendre ; l'écran en tire le message, pas le serveur. */
export class CodeEcoleError extends Error {
  constructor(public readonly code: CodeEcoleRefus) {
    super(code);
    this.name = 'CodeEcoleError';
  }
}

async function codeDuRefus(error: unknown): Promise<CodeEcoleRefus> {
  const contexte = (error as { context?: unknown }).context;
  if (contexte instanceof Response) {
    try {
      const corps = (await contexte.clone().json()) as { error?: string };
      if (corps.error && (CODES_CONNUS as readonly string[]).includes(corps.error)) return corps.error as CodeEcoleRefus;
    } catch {
      // corps illisible : refus inconnu
    }
  }
  return 'inconnu';
}

export async function lireLicenceEcole(): Promise<LicenceEcole | null> {
  const { data, error } = await supabase.functions.invoke<{ licence: LicenceEcole | null }>('licence-etudiant', { method: 'GET' });
  if (error) throw error;
  return data?.licence ?? null;
}

export async function accepterCodeEcole(code: string): Promise<{ organisationNom: string }> {
  const { data, error } = await supabase.functions.invoke<{ organisationNom: string }>('licence-etudiant', {
    body: { operation: 'accepter', code },
  });
  if (error) throw new CodeEcoleError(await codeDuRefus(error));
  if (!data) throw new CodeEcoleError('inconnu');
  return data;
}

export async function definirConsentementActivite(donne: boolean): Promise<void> {
  const { error } = await supabase.functions.invoke('licence-etudiant', { body: { operation: 'consentement', donne } });
  if (error) throw error;
}

// Rattachement de l'apprenti à son entreprise (0092). Le consentement au partage avec l'employeur est distinct de
// celui de l'activité : il ne se donne et ne se retire que par l'apprenti, et ne dépend pas de la licence.
export interface EmployeurApprenti {
  entrepriseNom: string;
  debutContrat: string;
  /** Vrai seulement sur accord explicite de l'apprenti ; jamais un défaut. */
  consentement: boolean;
}

export interface SituationEcole {
  licence: LicenceEcole | null;
  employeur: EmployeurApprenti | null;
}

/** Licence et employeur en un seul appel. Un serveur plus ancien ne renvoie pas `employeur` : rien à afficher. */
export async function lireSituationEcole(): Promise<SituationEcole> {
  const { data, error } = await supabase.functions.invoke<{ licence?: LicenceEcole | null; employeur?: EmployeurApprenti | null }>(
    'licence-etudiant',
    { method: 'GET' },
  );
  if (error) throw error;
  return { licence: data?.licence ?? null, employeur: data?.employeur ?? null };
}

export async function definirConsentementEmployeur(donne: boolean): Promise<void> {
  const { error } = await supabase.functions.invoke('licence-etudiant', { body: { operation: 'consentement_employeur', donne } });
  if (error) throw error;
}
